import { useMemo, useState } from "react";
import { Ruler, Trees, Trash2, Sparkles, Check, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, CALC } from "@/lib/config";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const formatDKK = (n) =>
  new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", maximumFractionDigits: 0 }).format(n);

export default function PriceCalculator({ compact = false }) {
  const [length, setLength] = useState(20);
  const [height, setHeight] = useState(CALC.heightOptions[0].value);
  const [includeWaste, setIncludeWaste] = useState(true);
  const [addOns, setAddOns] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });

  const selectedHeight = CALC.heightOptions.find((h) => h.value === height);

  const total = useMemo(() => {
    const base = length * CALC.basePricePerMeter * selectedHeight.multiplier;
    const wasteAdj = includeWaste ? 0 : -length * CALC.wasteRemovalDiscount;
    const addOnsTotal = addOns.reduce((sum, id) => {
      const s = CALC.additionalServices.find((a) => a.id === id);
      if (!s) return sum;
      if (s.pricePerMeter) return sum + s.pricePerMeter * length;
      return sum + (s.flatPrice || 0);
    }, 0);
    return Math.max(0, Math.round(base + wasteAdj + addOnsTotal));
  }, [length, selectedHeight, includeWaste, addOns]);

  const toggleAddOn = (id) => {
    setAddOns((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Udfyld venligst navn, e-mail og telefon.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        length_meters: length,
        height_category: selectedHeight.label,
        include_waste_removal: includeWaste,
        additional_services: addOns.map((id) => CALC.additionalServices.find((a) => a.id === id)?.label).filter(Boolean),
        estimated_price: total,
        notes: form.notes,
      });
      toast.success("Tak! Vi vender tilbage inden for 24 timer.");
      setQuoteOpen(false);
      setForm({ name: "", email: "", phone: "", address: "", notes: "" });
    } catch (err) {
      toast.error("Noget gik galt. Prøv venligst igen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="price-calculator"
      className={`grid gap-8 ${compact ? "" : "lg:grid-cols-[1.4fr_1fr]"}`}
    >
      {/* Controls */}
      <div className="bg-brand-surface rounded-3xl border border-brand-border p-6 sm:p-10">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-muted mb-3">
          <Sparkles className="h-3.5 w-3.5" /> Prisberegner
        </div>
        <h3 className="font-display text-3xl sm:text-4xl text-brand-text tracking-tight">
          Beregn din pris på hækklipning
        </h3>
        <p className="mt-2 text-brand-muted text-sm">
          Basispris {CALC.basePricePerMeter} kr/m — inkl. bortkørsel og oprydning.
        </p>

        {/* Length */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium flex items-center gap-2 text-brand-text">
              <Ruler className="h-4 w-4 text-brand-primary" /> Hækkens længde
            </Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="calc-length-input"
                type="number"
                min={1}
                max={500}
                value={length}
                onChange={(e) => setLength(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
                className="w-24 text-right"
              />
              <span className="text-sm text-brand-muted">meter</span>
            </div>
          </div>
          <Slider
            data-testid="calc-length-slider"
            value={[length]}
            min={1}
            max={200}
            step={1}
            onValueChange={(v) => setLength(v[0])}
            className="mt-2"
          />
          <div className="flex justify-between text-xs text-brand-muted mt-2">
            <span>1 m</span>
            <span>200 m</span>
          </div>
        </div>

        {/* Height */}
        <div className="mt-8">
          <Label className="text-sm font-medium flex items-center gap-2 text-brand-text mb-3">
            <Trees className="h-4 w-4 text-brand-primary" /> Hækkens højde
          </Label>
          <Select value={height} onValueChange={setHeight}>
            <SelectTrigger data-testid="calc-height-select" className="w-full h-12 bg-brand-bg border-brand-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CALC.heightOptions.map((h) => (
                <SelectItem key={h.value} value={h.value} data-testid={`calc-height-${h.value}`}>
                  {h.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Waste removal */}
        <div className="mt-8 flex items-start justify-between gap-4 rounded-2xl bg-brand-soft/60 border border-brand-border/60 p-5">
          <div className="flex gap-3">
            <Trash2 className="h-5 w-5 text-brand-primary mt-0.5" />
            <div>
              <div className="font-medium text-brand-text">Bortkørsel af haveaffald</div>
              <p className="text-sm text-brand-muted mt-1">
                Vi kører grene og affald væk og efterlader haven ryddelig.
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              data-testid="calc-waste-checkbox"
              checked={includeWaste}
              onCheckedChange={(v) => setIncludeWaste(Boolean(v))}
            />
          </label>
        </div>

        {/* Additional services */}
        <div className="mt-8">
          <Label className="text-sm font-medium text-brand-text mb-3 block">
            Ekstra tillæg (valgfrit)
          </Label>
          <div className="grid sm:grid-cols-2 gap-3">
            {CALC.additionalServices.map((s) => {
              const checked = addOns.includes(s.id);
              return (
                <button
                  type="button"
                  key={s.id}
                  data-testid={`calc-addon-${s.id}`}
                  onClick={() => toggleAddOn(s.id)}
                  className={`text-left rounded-2xl border p-4 transition-colors ${
                    checked
                      ? "border-brand-primary bg-brand-primary/5"
                      : "border-brand-border bg-brand-surface hover:bg-brand-soft/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-sm text-brand-text">{s.label}</div>
                      <div className="text-xs text-brand-muted mt-1">
                        {s.pricePerMeter ? `+${s.pricePerMeter} kr/m` : `+${s.flatPrice} kr`}
                      </div>
                    </div>
                    <span
                      className={`h-5 w-5 grid place-items-center rounded-full border transition-colors ${
                        checked ? "bg-brand-primary border-brand-primary text-brand-bg" : "border-brand-border"
                      }`}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="lg:sticky lg:top-28 h-fit">
        <div className="bg-brand-primary text-brand-bg rounded-3xl p-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-brand-bg/5" />
          <div className="absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-brand-bg/5" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-bg/70">Din estimerede pris</div>
            <div className="mt-3 font-display text-6xl tracking-tight" data-testid="calc-total">
              {formatDKK(total)}
            </div>
            <div className="text-sm text-brand-bg/70 mt-1">inkl. moms</div>

            <div className="mt-8 space-y-3 text-sm">
              <div className="flex items-center justify-between border-b border-brand-bg/15 pb-3">
                <span className="text-brand-bg/80">Længde</span>
                <span>{length} m</span>
              </div>
              <div className="flex items-center justify-between border-b border-brand-bg/15 pb-3">
                <span className="text-brand-bg/80">Højde</span>
                <span>{selectedHeight.label}</span>
              </div>
              <div className="flex items-center justify-between border-b border-brand-bg/15 pb-3">
                <span className="text-brand-bg/80">Bortkørsel</span>
                <span>{includeWaste ? "Inkluderet" : "Fravalgt"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-brand-bg/80">Tillæg</span>
                <span>{addOns.length || "Ingen"}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setQuoteOpen(true)}
              data-testid="calc-request-quote-btn"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-bg text-brand-primary px-6 py-3.5 font-medium hover:bg-brand-soft transition-all group"
            >
              Anmod om tilbud
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <p className="text-xs text-brand-bg/60 mt-3 text-center">
              Beregningen er vejledende. Endelig pris efter besigtigelse.
            </p>
          </div>
        </div>
      </div>

      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="sm:max-w-lg bg-brand-surface" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-text">Anmod om tilbud</DialogTitle>
            <DialogDescription>
              Vi vender tilbage inden for 24 timer. Estimeret pris: <strong>{formatDKK(total)}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitQuote} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="q-name" className="text-xs">Navn *</Label>
                <Input
                  id="q-name"
                  data-testid="quote-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="q-phone" className="text-xs">Telefon *</Label>
                <Input
                  id="q-phone"
                  data-testid="quote-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="q-email" className="text-xs">E-mail *</Label>
              <Input
                id="q-email"
                data-testid="quote-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="q-address" className="text-xs">Adresse *</Label>
              <Input
                id="q-address"
                data-testid="quote-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                required
                placeholder="Fx Havevej 12, 6400 Sønderborg"
              />
            </div>
            <div>
              <Label htmlFor="q-notes" className="text-xs">Bemærkninger</Label>
              <Textarea
                id="q-notes"
                data-testid="quote-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <DialogFooter>
              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary text-brand-bg px-6 py-3 font-medium hover:bg-brand-hover disabled:opacity-60"
              >
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sender...</> : "Send anmodning"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
